import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 *
 * Fenalor (05/10/2026, maquette validée par Enzo) : la barre se tient hors de la
 * zone d'achat, du titre de la fiche au vrai bouton, pour qu'il n'y ait jamais deux
 * titres ni deux boutons à l'écran. Elle est là dès l'affichage quand la zone est
 * sous elle (la galerie mobile s'arrête pile au-dessus), s'efface quand le titre
 * remonte au-dessus d'elle, revient dès que le vrai bouton commence à passer sous
 * l'en-tête, et reste au-dessus du pied de page. Elle n'est plus masquée par le chat Shopify
 * Inbox : c'est la bulle qui monte au-dessus d'elle (html[data-fnl-barre], styles
 * dans sections/product-information.liquid).
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {HTMLElement | null} */
  #zoneStart = null;

  /** @type {HTMLElement | null} */
  #buyButtons = null;

  /** @type {boolean} */
  #wanted = true;

  /** @type {number | null} */
  #lastZoneTop = null;

  /** @type {number | null} */
  #frame = null;

  /** @type {ResizeObserver | null} */
  #barObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#setupVisibility();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#barObserver?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    document.documentElement.removeAttribute('data-fnl-barre');
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the visibility rule: the bar stays out of the purchase zone,
   * from the brand line to the main buy buttons.
   */
  #setupVisibility() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!(buyButtonsBlock instanceof HTMLElement)) return;

    this.#buyButtons = buyButtonsBlock;
    this.#resolveZone();
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    const { signal } = this.#abortController;
    // Sur ordinateur, la page défile dans .page-wrapper et non dans la fenêtre : on écoute
    // tout défilement, en phase de capture (un défilement d'élément ne remonte pas).
    document.addEventListener('scroll', this.#scheduleUpdate, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });

    // La hauteur de la barre change quand la police arrive ou que la finition change :
    // la bulle de chat et la marge du pied de page la suivent.
    this.#barObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#barObserver.observe(this.refs.stickyBar);

    this.#update(true);
  }

  /**
   * Finds the purchase zone again when the theme has re-rendered it: a change of
   * finish replaces the product details, and a detached element measures as zero.
   */
  #resolveZone() {
    if (!this.#buyButtons?.isConnected) {
      const block = this.#getProductForm()?.closest('.buy-buttons-block');
      this.#buyButtons = block instanceof HTMLElement ? block : null;
    }
    if (!this.#zoneStart?.isConnected) {
      const section = this.closest('.shopify-section');
      this.#zoneStart =
        section?.querySelector('.product-details h1') ??
        section?.querySelector('.product-details .fen-brand') ??
        section?.querySelector('.product-details') ??
        this.#buyButtons;
    }
  }

  #scheduleUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#update(false);
    });
  };

  /**
   * Applies the rule, with a margin each way so the bar never flickers.
   * @param {boolean} initial - First measure: no previous state to keep
   */
  #update(initial) {
    this.#resolveZone();
    const zone = this.#zoneStart;
    const buyButtons = this.#buyButtons;
    if (!zone || !buyButtons) return;

    const barHeight = this.refs.stickyBar.offsetHeight;
    const header = document.querySelector('header-component');
    const headerBottom = Math.max(0, header ? header.getBoundingClientRect().bottom : 0);
    const zoneTop = zone.getBoundingClientRect().top;
    const buyTop = buyButtons.getBoundingClientRect().top;
    const edge = window.innerHeight - barHeight;

    // Sens du défilement, lu sur la page et non sur la fenêtre : sur iPhone, la barre
    // d'adresse de Safari se replie quand on descend et agrandit l'écran sans que la page
    // bouge. Seule une vraie remontée peut faire revenir la barre avant la fiche.
    const goingUp = this.#lastZoneTop !== null && zoneTop > this.#lastZoneTop + 0.5;
    this.#lastZoneTop = zoneTop;

    // Avant la zone : le titre est encore sous le bord de la barre (20 px de marge).
    // Après : le vrai bouton commence à passer sous l'en-tête.
    const beforeZone = zoneTop >= edge - 20;
    const afterZone = buyTop < headerBottom;

    let wanted = this.#wanted;
    if (initial) {
      wanted = beforeZone || afterZone;
    } else if (wanted) {
      // 14 px de marge sous l'en-tête pour que la barre ne clignote pas autour du bouton
      if (!beforeZone && buyTop > headerBottom + 14) wanted = false;
    } else if (afterZone || (goingUp && zoneTop > edge - 6)) {
      wanted = true;
    }

    this.#wanted = wanted;
    if (barHeight > 0) document.documentElement.style.setProperty('--fnl-barre-h', `${barHeight}px`);
    if (wanted) this.#showStickyBar();
    else this.#hideStickyBar();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    // Sur mobile la vignette est masquée : pas d'envol vers le panier, la coche reste 1,6 s
    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage?.offsetParent) {
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 1600);
      return;
    }

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
        this.#scheduleUpdate();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    if (stickyBar.dataset.stuck !== 'true') stickyBar.dataset.stuck = 'true';
    document.documentElement.toggleAttribute('data-fnl-barre', this.dataset.variantAvailable === 'true');
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    if (stickyBar.dataset.stuck !== 'false') stickyBar.dataset.stuck = 'false';
    document.documentElement.removeAttribute('data-fnl-barre');
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
